import { Component, Input, OnInit, ViewEncapsulation, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { loadStripe, Stripe, StripeCardElement, StripeElement, StripeElements } from '@stripe/stripe-js';
import { throwError } from 'rxjs';
import { InterestedPerson, Location, PaymentIntentResponse, PaymentRequest, SiteMeta } from 'src/app/models/common-models';
import { TermUnit } from 'src/app/models/enums';
import { Location as LocationV2 } from 'src/app/models/location';
import { MenuPack, Pack } from 'src/app/models/meta';
import { TaxRegion } from 'src/app/models/tax';
import { LogService } from 'src/app/services/common/log.service';
import { Utils } from 'src/app/services/common/utils';
import { EnvironmentService } from 'src/app/services/environment.service';
import { InterestedPersonService } from 'src/app/services/interested-person.service';
import { StripeService } from 'src/app/services/stripe.service';

@Component({
  selector: 'app-payment-form',
  templateUrl: './payment-form.component.html',
  styleUrls: ['./payment-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PaymentFormComponent implements OnInit {
  
  @Input() public locationId : number;
  @Input() public pack : Pack;
  @Input() public menuPack : MenuPack;
  @Input() public location : Location;
  @Input() public locationV2 : LocationV2;
  @Input() public taxRegion : TaxRegion;
  @Input() public siteMeta : SiteMeta;
  @Input() public rateLockFees : number = 0;
  @Output() close = new EventEmitter<void>();

  isLoading = true;
  paymentIntentCreated = false;
  paymentDetailsAdded = false;
  submitted: boolean = false;
  
  stripe!: Stripe | null;
  elements!: StripeElements;
  cardElement!: StripeCardElement;
  paymentElement!: StripeElement | null;
  errorMessage: string | null = null;

  processingFees: number = 0;
  processingFeesPercentage: number = 0;
  taxAmount: number = 0;
  agreedTermsOfService: boolean = false;
  isSmsNotificationVisible: boolean = false;

  paymentForm!: FormGroup;
  paymentRequest: PaymentRequest = { MenuPackId: 0, Amount: 0, Currency: 'usd', FirstName: '', LastName: '', Email: '', DialCode: '+1', PhoneNumber: '', EmailNotification: true, SmsNotification: false, PaymentMethod: '' };
  paymentMethods: any[] = [];
  selectedPaymentMethodId?: string = "";
  clientSecret: string;

  paymentIntentId: string = "";
  isButtonDisabled: boolean = true;
  connectedAccountName: string = ''

  countryCode = 'us';
  flagUrl: string = `https://dropfitness-static.nyc3.digitaloceanspaces.com/static/svg/flags/4x3/us.svg`;
  dialCode = "+1";

  isOrderDetailsVisible: boolean = true;

  logo = '';

  constructor(
    private stripeService: StripeService,
    private logService: LogService,
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    public activeModal: NgbActiveModal,
    private _environmentService: EnvironmentService,
    private interestedPersonService: InterestedPersonService
  ) {
    
  }

  async ngOnInit() {
    // Check if prospectInfo exists in local storage
    const prospectInfo = sessionStorage.getItem('prospectInfo');
    if (prospectInfo) {
      try {
        const parsedInfo = JSON.parse(prospectInfo);
        this.paymentRequest = {
          ...this.paymentRequest,
          FirstName: parsedInfo.FirstName || '',
          LastName: parsedInfo.LastName || '',
          Email: parsedInfo.Email || '',
          DialCode: parsedInfo.DialCode || '+1',
          PhoneNumber: parsedInfo.PhoneNumber || '',
          EmailNotification: parsedInfo.EmailNotification !== undefined ? parsedInfo.EmailNotification : true,
          SmsNotification: parsedInfo.SmsNotification !== undefined ? parsedInfo.SmsNotification : false,
        };
      } catch (error) {
        console.error('Error parsing prospectInfo from local storage:', error);
      }
    }
  
    this.stripe = await loadStripe(this._environmentService.getFacilityConfig().stripe.publishedKey, { stripeAccount: this._environmentService.getStripeAccountId() }); // Replace with your publishable key
    if (this.pack.IsTaxable) {
      // Zero tax for rate lock fee.
      const rateLockTaxRate = 0; // this.taxRegion.TaxRate
      this.taxAmount = this.rateLockFees * (rateLockTaxRate / 100);
    }
    this.paymentRequest.MenuPackId = this.menuPack.Id;
    this.paymentRequest.Amount = this.rateLockFees + this.taxAmount + this.processingFees;
    this.paymentRequest.Currency = 'usd';
    this.initForm(this.paymentRequest);
    this.processingFeesPercentage = this.locationV2.credit_card_surcharge;
    this.connectedAccountName = this.locationV2.description || this.connectedAccountName;
    this.validateField('FirstName');
    this.isLoading = false;
    this.logo = this._environmentService.getLinkUrlById('smallLogo');
    this.connectedAccountName = this._environmentService.getFacilityConfig().name || 'Drop Fitness';
  }

  initForm(paymentRequest?: PaymentRequest): void {
    const initial: PaymentRequest = paymentRequest || {
      MenuPackId: 0, Amount: 0, Currency: 'usd', FirstName: '', LastName: '', Email: '', DialCode: '+1', PhoneNumber: '', EmailNotification: true, SmsNotification: false, PaymentMethod: 'us_bank_account'
    };
    this.paymentRequest = initial;
    
    this.paymentForm = this.fb.group({
      Amount: [initial.Amount, [Validators.required]],
      Currency: [initial.Currency, [Validators.required]],
      FirstName: [initial.FirstName, [Validators.required]],
      LastName: [initial.LastName, [Validators.required]],
      Email: [initial.Email, [Validators.required, Validators.email]],
      DialCode: [initial.DialCode, [Validators.required]],
      PhoneNumber: [initial.PhoneNumber, [Validators.required, Validators.pattern('^[0-9]{10}$')]],
      MenuPackId: [initial.MenuPackId, [Validators.required]],
      PaymentMethod: [initial.PaymentMethod, [Validators.required]],
      EmailNotification: [initial.EmailNotification, []],
      SmsNotification: [initial.SmsNotification, []],
    });
  }

  // To trigger validation on focus out
  validateField(fieldName: string): void {
    const control = this.paymentForm.get(fieldName);
    if (control) {
      control.markAsTouched();
      control.updateValueAndValidity();
    }

    // Check if both fields are valid
    if (this.paymentForm.get('FirstName')?.valid && this.paymentForm.get('LastName')?.valid && this.paymentForm.get('Email')?.valid && this.paymentForm.get('DialCode')?.valid && this.paymentForm.get('PhoneNumber')?.valid) {
      var request : Partial<InterestedPerson> = {
        LocationId: this.locationId,
        DialCode: this.paymentForm.get('DialCode')?.value,
        PhoneNumber: this.paymentForm.get('PhoneNumber')?.value,
        FirstName: this.paymentForm.get('FirstName')?.value,
        LastName: this.paymentForm.get('LastName')?.value,
        Email: this.paymentForm.get('Email')?.value,
        MenuPackId: this.paymentForm.get('MenuPackId')?.value,
        EmailNotification: this.paymentForm.get('EmailNotification')?.value,
        SmsNotification: this.paymentForm.get('SmsNotification')?.value,
        PaymentMethod: this.paymentForm.get('PaymentMethod')?.value,
        PurchaseId: "",
        Amount: this.paymentForm.get('Amount')?.value,
        PurchaseDescription: "Rate Lock Fee " + (this.menuPack?.Name ? "for " + this.menuPack?.Name : "Rate Lock Fee")
      };
      this.interestedPersonService.saveInterestedPerson(request).subscribe(async (result: InterestedPerson) => {
        this.createPaymentIntent();
      });
    }
  }

  onTermsOfServiceChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    this.agreedTermsOfService = checkbox.checked;
    this.submitButtonToggle();
  }

  onSmsNotificationChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    this.isSmsNotificationVisible = checkbox.checked;
  }

  submitButtonToggle(): void {
    if (
      this.paymentForm.get('FirstName')?.valid &&
      this.paymentForm.get('LastName')?.valid &&
      this.paymentForm.get('Email')?.valid &&
      this.paymentForm.get('DialCode')?.valid &&
      this.paymentForm.get('PhoneNumber')?.valid &&
      !this.isLoading &&
      this.paymentIntentCreated &&
      this.agreedTermsOfService &&
      this.paymentDetailsAdded
    ) {
      this.isButtonDisabled = false;
    } else {
      this.isButtonDisabled = true;
    }
  }

  async createPaymentIntent() {
    this.isLoading = true;
    const initialAmount = this.rateLockFees + this.taxAmount + this.processingFees;
    this.stripeService.createPaymentIntent({ ...this.paymentForm.value, Amount: initialAmount }).subscribe((result: PaymentIntentResponse) => {
      if (this.stripe) {
        this.paymentIntentCreated = true;
        this.paymentIntentId = result.Id;
        this.paymentMethods = result.PaymentMethods;
        this.clientSecret = result.ClientSecret;
        this.elements = this.stripe.elements({
          clientSecret: result.ClientSecret,
        });
        this.createPaymentElement();
      }
      this.isLoading = false;
    });
  }

  createPaymentElement() {
    try {
      const connectedAccountName = this.connectedAccountName || 'Drop Fitness'; // Ensure a fallback name is used
      this.paymentElement = this.elements.create('payment', {
        terms: {
          card: 'always',
          usBankAccount: 'auto'
        },
        fields: {
          billingDetails: {
            name: 'auto',
          }
        },
        paymentMethodOrder: ['us_bank_account', 'card'],
        business: {
          name: connectedAccountName // Explicitly set the connected account name
        }
      });
      this.paymentElement.mount('#payment-element');

      this.paymentElement.on('change', (event: any) => {
        this.paymentDetailsAdded = event.complete;

        if (event.value && event.value.type) {
          this.handlePaymentMethodChange(event.value.type);
        }
        if (event.error) {
          this.logService.error('Error from stripe : ', event.error.message);
        }
        this.submitButtonToggle();
      });
      this.isLoading = false;

      this.submitButtonToggle();
    } catch (error) {
      console.error('Payment Element initialization failed:', error);
      this.paymentElement = null;
    }
  }

  handlePaymentMethodChange(paymentMethodType: string) {
    if (paymentMethodType === 'card') {
      this.selectedPaymentMethodId = ''; 
      this.processingFees = this.rateLockFees * (this.processingFeesPercentage / 100); 
    } else if (paymentMethodType === 'us_bank_account') {
      this.processingFees = this.rateLockFees * (0 / 100); 
      this.selectedPaymentMethodId = ''; 
    }
    this.paymentForm.patchValue({
      PaymentMethod: paymentMethodType,
    });
    this.paymentRequest.PaymentMethod = paymentMethodType;
    this.updatePaymentIntentAmount(); 
  }

  updatePaymentIntentAmount() {
    const updatedAmount = this.rateLockFees + this.taxAmount + this.processingFees;
    this.stripeService.updatePaymentIntent(this.paymentIntentId, updatedAmount, this.paymentForm.get('PaymentMethod')?.value).subscribe({
      next: () => {
        this.paymentRequest.Amount = updatedAmount;
        this.paymentRequest.PaymentMethod = this.paymentForm.get('PaymentMethod')?.value;
      },
      error: (err) => {
        console.error('Failed to update payment intent amount:', err);
      },
    });
  }

  async handlePayment() {
    if (!this.stripe || !this.paymentElement) {
      console.error('Stripe or Payment Element not initialized.');
      return;
    }
  
    try {
      let result;
      if (this.selectedPaymentMethodId) {
        this.isButtonDisabled = true;
        result = await this.stripe.confirmPayment({
          clientSecret: this.clientSecret,
          confirmParams: {
            payment_method: this.selectedPaymentMethodId,
            return_url: `${window.location.origin}/purchase?success=true&id=${this.paymentIntentId}`,
            receipt_email: this.paymentForm.get('Email')?.value,
          }
        });
      } else if (this.paymentElement) {
        this.isButtonDisabled = true;
        result = await this.stripe.confirmPayment({
          elements: this.elements,
          confirmParams: {
            return_url: `${window.location.origin}/purchase?success=true&id=${this.paymentIntentId}`,
            receipt_email: this.paymentForm.get('Email')?.value,
          },
          redirect: 'if_required',
        });
      } else {
        console.error('No payment method selected or Payment Element initialized.');
        return;
      }
  
      if (result.error) {
        this.isButtonDisabled = false;
        console.error('Payment confirmation failed:', result.error.message);
        alert('Payment failed: ' + result.error.message);
        window.location.href = `${window.location.origin}/purchase?success=false&error=${result.error.message}`;
        return;
      } else if (result.paymentIntent) {
        // Remove prospectInfo from local storage on successful payment
        localStorage.removeItem('prospectInfo');
        window.location.href = `${window.location.origin}/purchase?success=true&id=${this.paymentIntentId}`;
        return;
      }
    } catch (error) {
      this.isButtonDisabled = false;
      this.errorMessage = 'Failed to process bank payment.';
    }
  }

  getTermDuration() {
    var unit = Utils.getEnumNameFromValue(TermUnit, this.menuPack.TermUnit).toLowerCase();
    switch(unit){
      case "month":
        return "/mo.";
      case "year":
        return "/yr.";
      default:
        return "/mo.";
    }
  }
  
  formatCurrency(amount: number){
    return Utils.formatCurrency(amount);
  }

  toTitleCase(input: string){
    return Utils.toTitleCase(input);
  }

  showPaymentElement() {
    const paymentContainer = document.getElementById('payment-element');
    if (paymentContainer) {
      paymentContainer.style.display = 'block'; 
    }
  }
  
  hidePaymentElement() {
    const paymentContainer = document.getElementById('payment-element');
    if (paymentContainer) {
      paymentContainer.style.display = 'none'; 
    }
  }

  async onSubmit() {
    if (!this.stripe || !this.paymentElement) {
      console.error('Stripe or Payment Element not initialized.');
      return;
    }

    if(!this.paymentForm.valid)
    {
      this.isButtonDisabled = true;
      return;
    }
    try {
      await this.handlePayment();
    } catch (err) {
      this.logService.error('Error fetching data', err);
      return throwError(() => err);
    } finally {
      return;
    }
  }

  public open() {
  }

  flag(): string {
    return `https://dropfitness-static.nyc3.digitaloceanspaces.com/static/svg/flags/4x3/${this.countryCode}.svg`;
  }

  changeCountry(ctry: any) {
    this.countryCode = ctry.country;
    this.flagUrl = `https://dropfitness-static.nyc3.digitaloceanspaces.com/static/svg/flags/4x3/${this.countryCode}.svg`;
    this.dialCode = ctry.phoneCode;
    this.paymentForm.patchValue({
      DialCode: ctry.phoneCode
    });
  }

  toggleOrderDetails(): void {
    this.isOrderDetailsVisible = !this.isOrderDetailsVisible;
  }

  closeModal(result: boolean) {
    this.activeModal.close(result);
  }

  getMenuPackNameById()
  {
    return Utils.newLineToBr(this.menuPack.Name, this.sanitizer);
  }

  getPackDescriptionById()
  {
    return Utils.newLineToBr(this.pack.Description, this.sanitizer);
  }

  getPackExpiryLabel()
  {
    return "Expires in " + this.menuPack.TermDuration + " " + Utils.getEnumNameFromValue(TermUnit, this.menuPack.TermUnit) + (this.menuPack.TermDuration > 1 ? "s" : "");
  }

  getPackImage()
  {
    
    return this.pack?.Image;
    
  }

  closeForm() {
    this.close.emit();
  }
}
